import * as S from './MenuItems.styles'
import { MenuItem } from '../MenuItem/MenuItem'
import {
  ArtIcon,
  BookIcon,
  ChatIcon,
  GalleryWallsIcon,
  GiftIcon,
  HistoryIcon,
  PercentageMarkIcon,
  PhotoIcon,
  QuestionMarkIcon,
  ReferralIcon,
} from '../../../icons'
import { useTranslation } from 'react-i18next'
import {
  DISCOVERY_URL,
  AI_GENERATION_URL,
  GIFT_CARD,
  MY_ORDERS_URL,
  ONBOARDING_URL,
  PHOTO_STYLER_URL,
  PHOTO_STYLER_HOMEPAGE_URL,
  REFERRAL,
  ART_COLLECTION_HOME_URL,
  AI_PLACES_URL,
  AI_KIDS_URL,
  AI_VINTAGE_POSTERS_URL,
  EXTERNAL_WALL_DESIGNER_URL,
} from '../../../config/config'
import StorageManager from '../../../services/StorageManager'
import { ACTIVE_ORDERS_COUNT_KEY } from '../../../services/LoginUtills'
import { emailCaptureManager } from '../../../services/EmailCaptureManager'
import { MENU_ITEMS } from './MenuItems.consts'
import { QUIZ_STEPS } from '../../../pages/EmailCapturePage/EmailCapturePage.consts'
import { intercomService } from '../../../services/Analytics/platforms/intercom'
import { useKeys } from 'services/KeysProvider'
import { useExperimentManager } from '../../../services/ExperimentManager/ExperimentManager'
import { getPhotoBookURL } from '../../../services/PhotoBook/photoBookUtils'
import { useTilesInProgress } from 'hooks/useWorkInProgress'
import useMultiProductHomePageExperiment from 'pages/MultiProductHomePage/useMultiProductHomePageExperiment'
import { MULTI_PRODUCT_HOMEPAGE_SOURCE } from 'pages/MultiProductHomePage/multiProductHomePage.consts'
import { getReferralPrice } from '../../../pages/Referral/referralUtils'
import { usePricing } from 'components/Pricing/PricingProvider'
import { NavDivider } from '../Divider.styles'
import { useLocation, useNavigate } from 'react-router'
import {
  AIPetsIcon,
  AINewbornIcon,
  AIFamilyIcon,
  AIPlacesIcon,
  AITravelIcon,
  AIVintagePostersIcon,
  AIHomeHobbiesPlantIcon,
  AIHomeHobbiesBowlIcon,
  AIHomeHobbiesHouseIcon,
  AIFoodCutleryIcon,
  AIFoodPlaceSettingIcon,
  AIFoodPlaceSettingLargeIcon,
  AIFoodPotIcon,
  AIFoodWineGlassIcon,
  AIFoodRecipeCardIcon,
} from './MenuItems.styles'

const MENU_ICONS = new Map([
  ['pets', AIPetsIcon],
  ['newborn', AINewbornIcon],
  ['family', AIFamilyIcon],
  ['places', AIPlacesIcon],
  ['travel', AITravelIcon],
  ['vintage-posters', AIVintagePostersIcon],
  ['home-hobbies-plant', AIHomeHobbiesPlantIcon],
  ['home-hobbies-bowl', AIHomeHobbiesBowlIcon],
  ['home-hobbies-house', AIHomeHobbiesHouseIcon],
  ['food-cutlery', AIFoodCutleryIcon],
  ['food-place-setting', AIFoodPlaceSettingIcon],
  ['food-place-setting-large', AIFoodPlaceSettingLargeIcon],
  ['food-pot', AIFoodPotIcon],
  ['food-wine-glass', AIFoodWineGlassIcon],
  ['food-recipe-card', AIFoodRecipeCardIcon],
])

// exists() resolves through fallbackLng, so it passes keys that only exist in
// English; getResource reads the active language's bundle directly.
function resolveTextKey(i18n, payloadTextKey, fallbackKey) {
  return payloadTextKey &&
    i18n.getResource(i18n.resolvedLanguage, 'translation', payloadTextKey)
    ? payloadTextKey
    : fallbackKey
}

let intercomShowCallBackSet = false
let showHelp = false

export default function MenuItems({ isOpen, onDismissMenu, onPromoCodeClick }) {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()
  const { ipCountry } = useKeys()
  const isJapan = ipCountry === 'JP'
  const experimentManager = useExperimentManager()
  const isPhotoBooksNewBadgeEnabled = experimentManager.isEnabled(
    'photo-books-new-badge'
  )
  const { pathname } = useLocation()
  const { isEnabled: isMultiProductHomepageEnabled } =
    useMultiProductHomePageExperiment()
  // Route "Frame your photos" to the tiles homepage only when the menu is opened
  // from the multi-product homepage itself (root route). Anywhere else the item
  // keeps its normal behavior of going straight to the tile designer.
  const isOnMultiProductHomepage =
    isMultiProductHomepageEnabled && pathname === '/'
  const hasTilesInProgress = useTilesInProgress()
  // Before any tiles exist, open the tiles homepage instead of the designer, and
  // ride the same source state as the homepage's "Learn more" CTA so the tiles
  // homepage skips the /products step. Once tiles are in progress, go straight to
  // the designer (same behavior as photo books).
  const opensTilesHomepage = isOnMultiProductHomepage && !hasTilesInProgress
  const framePhotosUrl = opensTilesHomepage
    ? PHOTO_STYLER_HOMEPAGE_URL
    : PHOTO_STYLER_URL
  const framePhotosSource = opensTilesHomepage
    ? MULTI_PRODUCT_HOMEPAGE_SOURCE
    : 'Menu'
  const { pricingData } = usePricing()
  const { currency } = pricingData
  const handleTalkToUsClicked = () => {
    intercomService.show()
    onDismissMenu()
  }

  const handleHelpCenterClick = () => {
    // Work around for non-working help center.
    // The workaround is to show the help center in the home space first, then show the help space.
    // should be replaced with intercomService.showSpace('help')
    showHelp = true
    if (!intercomShowCallBackSet) {
      intercomService.setOnShowEvent(() => {
        if (showHelp) {
          showHelp = false
          setTimeout(() => intercomService.showSpace('help'), 500)
        }
      })
      intercomShowCallBackSet = true
    }

    intercomService.show()
    onDismissMenu()
  }

  const questionBeforeRedirect = (e, returnToPath) => {
    if (!emailCaptureManager.passedAnyOnboarding()) {
      // Redirect user to onboarding if they should pass it before continuing to the target url
      e.preventDefault()
      const searchParams = new URLSearchParams({
        redirectTo: returnToPath,
      })
      navigate({
        pathname: `${ONBOARDING_URL}/${QUIZ_STEPS.INTRO}`,
        search: searchParams.toString(),
      })
    }
  }

  // The menu stays mounted on every page for SEO; reading the flag only while it is open
  // keeps visitors who never open it out of the experiment.
  const isExternalWallDesignerEnabled =
    isOpen && experimentManager.isEnabled('external-wall-designer')
  const galleryWallsUrl = isExternalWallDesignerEnabled
    ? EXTERNAL_WALL_DESIGNER_URL
    : DISCOVERY_URL

  const galleryWallsItem = (
    <S.Item>
      <MenuItem
        itemId={MENU_ITEMS.GALLERY_WALLS}
        icon={<GalleryWallsIcon />}
        text={t('pitch.footer.gallery_walls')}
        location={
          isExternalWallDesignerEnabled
            ? EXTERNAL_WALL_DESIGNER_URL
            : { pathname: DISCOVERY_URL, state: { source: 'Menu' } }
        }
        isExternal={isExternalWallDesignerEnabled}
        onClick={(e) => questionBeforeRedirect(e, galleryWallsUrl)}
        testId="gallery-walls-menu-item"
      />
    </S.Item>
  )

  const familyPortraitsItem = (
    <S.Item>
      <MenuItem
        itemId={MENU_ITEMS.AI_GENERATION_CHRISTMAS}
        icon={<AIFamilyIcon />}
        text={t('ai_templates.menu_title_family')}
        location={{
          pathname: `${AI_GENERATION_URL}/family`,
          state: { source: 'Menu' },
        }}
        onClick={(e) =>
          questionBeforeRedirect(e, `${AI_GENERATION_URL}/family`)
        }
      />
    </S.Item>
  )

  const isVintagePosters = experimentManager.isEnabled(
    'ai-templates-vintage-posters'
  )
  const kidsPayload = experimentManager.getVariantPayload(
    'ai-templates-vintage-posters'
  )
  const kidsSlotUrl =
    kidsPayload?.url ||
    (isVintagePosters ? AI_VINTAGE_POSTERS_URL : AI_KIDS_URL)
  const kidsSlotTextKey = resolveTextKey(
    i18n,
    kidsPayload?.textKey,
    isVintagePosters
      ? 'ai_templates.menu_title_vintage_posters'
      : 'ai_templates.menu_title_kids'
  )
  const KidsSlotIcon =
    MENU_ICONS.get(kidsPayload?.iconKey) ||
    (isVintagePosters ? AIVintagePostersIcon : AINewbornIcon)

  const kidsWallArtItem = (
    <S.Item>
      <MenuItem
        itemId={MENU_ITEMS.AI_GENERATION_KIDS}
        icon={<KidsSlotIcon />}
        text={t(kidsSlotTextKey)}
        location={kidsSlotUrl}
        isExternal
        onClick={(e) => questionBeforeRedirect(e, kidsSlotUrl)}
      />
    </S.Item>
  )

  const placesVariant = experimentManager.getVariantKey('ai-templates-places')
  const isTravelPosters = placesVariant === 'travel-posters'
  const placesPayload = experimentManager.getVariantPayload(
    'ai-templates-places'
  )
  const placesSlotUrl = placesPayload?.url || AI_PLACES_URL
  const placesSlotTextKey = resolveTextKey(
    i18n,
    placesPayload?.textKey,
    isTravelPosters
      ? 'ai_templates.menu_title_travel_posters'
      : 'ai_templates.menu_title_places'
  )
  const PlacesSlotIcon =
    MENU_ICONS.get(placesPayload?.iconKey) ||
    (isTravelPosters ? AITravelIcon : AIPlacesIcon)

  const aiPlacesItem = placesVariant !== 'off' && (
    <S.Item>
      <MenuItem
        itemId={MENU_ITEMS.AI_GENERATION_PLACES}
        icon={<PlacesSlotIcon />}
        text={t(placesSlotTextKey)}
        location={placesSlotUrl}
        isExternal
        onClick={(e) => questionBeforeRedirect(e, placesSlotUrl)}
      />
    </S.Item>
  )

  const artCollectionItem = (
    <S.Item>
      <MenuItem
        itemId={MENU_ITEMS.ART}
        icon={<ArtIcon />}
        text={t('general.menu.art_collection')}
        location={{
          pathname: ART_COLLECTION_HOME_URL,
          search: '?arrivedFrom=Classic Menu',
        }}
        testId="browse-art"
      />
    </S.Item>
  )

  return (
    <S.Container>
      <S.Section>
        <S.Item>
          <MenuItem
            itemId={MENU_ITEMS.MIXTILES_CLASSIC}
            icon={<PhotoIcon />}
            text={t('general.menu.frame_your_photos')}
            location={{
              pathname: framePhotosUrl,
              state: { source: framePhotosSource },
            }}
            onClick={
              isOnMultiProductHomepage
                ? undefined
                : (e) => questionBeforeRedirect(e, PHOTO_STYLER_URL)
            }
            testId="frame-your-photos"
          />
        </S.Item>
        {experimentManager.isEnabled('photo-books') && (
          <S.Item>
            <MenuItem
              itemId={MENU_ITEMS.PHOTO_BOOK}
              icon={<BookIcon />}
              text={t('product_selection.photo_books_name')}
              location={{
                pathname: getPhotoBookURL(),
                state: { source: 'Menu' },
              }}
              isNew={isPhotoBooksNewBadgeEnabled}
              testId="photo-books-menu-item"
            />
          </S.Item>
        )}
        <S.Item>
          <MenuItem
            itemId={MENU_ITEMS.AI_GENERATION}
            icon={<AIPetsIcon />}
            text={t('ai_templates.menu_title_pet')}
            location={{
              pathname: AI_GENERATION_URL,
              state: { source: 'Menu' },
            }}
            onClick={(e) => questionBeforeRedirect(e, AI_GENERATION_URL)}
          />
        </S.Item>
        {galleryWallsItem}
        {!isJapan && familyPortraitsItem}
        {!isJapan && aiPlacesItem}
        {!isJapan && kidsWallArtItem}
        {artCollectionItem}
      </S.Section>
      <NavDivider />
      <S.Section>
        {!isJapan && (
          <S.Item>
            <MenuItem
              itemId={MENU_ITEMS.GIFT_CARD}
              icon={<GiftIcon />}
              text={t('general.menu.buy_gift_card')}
              location={{ pathname: GIFT_CARD }}
              testId="gift-card-menu-item"
            />
          </S.Item>
        )}
        <S.Item>
          <MenuItem
            itemId={MENU_ITEMS.PROMO_CODE}
            icon={<PercentageMarkIcon />}
            text={t('general.menu.promo_code_manager.promo_codes')}
            onClick={onPromoCodeClick}
            testId="add-promo-menu-item"
          />
        </S.Item>
        <S.Item>
          <MenuItem
            itemId={MENU_ITEMS.MY_ORDERS}
            icon={<HistoryIcon />}
            text={t('general.menu.my_orders')}
            location={{ pathname: MY_ORDERS_URL }}
            tag={StorageManager.getWithDefaultValue(ACTIVE_ORDERS_COUNT_KEY, 0)}
            testId="my-orders-menu-item"
          />
        </S.Item>
        {experimentManager.isEnabled('referral') && (
          <S.Item>
            <MenuItem
              itemId={MENU_ITEMS.REFERRAL}
              icon={<ReferralIcon />}
              text={t('referral.title', { v1: getReferralPrice(currency) })}
              location={{ pathname: REFERRAL }}
              testId="referral-menu-item"
            />
          </S.Item>
        )}
        <S.Item>
          <MenuItem
            itemId={MENU_ITEMS.HELP_CENTER}
            icon={<QuestionMarkIcon />}
            text={t('general.menu.help_center')}
            onClick={handleHelpCenterClick}
            testId="help-center-menu-item"
          />
        </S.Item>
        <S.Item>
          <MenuItem
            itemId={MENU_ITEMS.CHAT_WITH_US}
            icon={<ChatIcon />}
            text={t('general.menu.chat_with_us')}
            onClick={handleTalkToUsClicked}
            testId="chat-menu-item"
          />
        </S.Item>
      </S.Section>
    </S.Container>
  )
}
